import React, { useState, useCallback } from 'react';
import { Box, IconButton, Typography } from '@mui/material';

interface FireEmojiSliderProps {
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
  size?: 'small' | 'medium';
}

const FIRE = '🔥';
const MIN = 1;
const MAX = 5;

/**
 * FireEmojiSlider — a 1–5 scale rating input using fire emojis.
 * Each level renders the corresponding number of 🔥 emojis.
 * Submits rating on click/tap of a level.
 */
const FireEmojiSlider: React.FC<FireEmojiSliderProps> = ({
  value,
  onChange,
  disabled = false,
  size = 'medium',
}) => {
  const [hoverValue, setHoverValue] = useState<number | null>(null);
  const displayValue = hoverValue ?? value;
  const emojiSize = size === 'small' ? '1rem' : '1.4rem';

  const handleSelect = useCallback(
    (level: number) => {
      if (disabled) return;
      const clamped = Math.max(MIN, Math.min(MAX, Math.round(level)));
      onChange(clamped);
    },
    [disabled, onChange],
  );

  return (
    <Box
      sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}
      role="radiogroup"
      aria-label="Rate this outfit with fire emojis"
    >
      {Array.from({ length: MAX }, (_, i) => {
        const level = i + 1;
        const isActive = level <= displayValue;
        return (
          <IconButton
            key={level}
            onClick={() => handleSelect(level)}
            onMouseEnter={() => !disabled && setHoverValue(level)}
            onMouseLeave={() => setHoverValue(null)}
            disabled={disabled}
            role="radio"
            aria-checked={level === value}
            aria-label={`Rate ${level} out of 5`}
            sx={{
              p: 0.5,
              minWidth: 32,
              minHeight: 32,
              opacity: isActive ? 1 : 0.3,
              transform: isActive ? 'scale(1.1)' : 'scale(0.9)',
              transition: 'opacity 150ms ease, transform 150ms ease',
              '@media (prefers-reduced-motion: reduce)': {
                transition: 'none',
              },
            }}
          >
            <Typography
              component="span"
              sx={{ fontSize: emojiSize, lineHeight: 1, userSelect: 'none' }}
            >
              {FIRE}
            </Typography>
          </IconButton>
        );
      })}
    </Box>
  );
};

export default FireEmojiSlider;
